import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import { buyerApi } from '../Buyer/BuyerService';
import type {
  BuyerEnquiryDetail,
  BuyerEnquiryRow,
  CreateBuyerEnquiryArgs,
  CreatedBuyerEnquiry,
  RespondBuyerEnquiryArgs
} from './buyerEnquiryTypes';

/**
 * The buyer's half of the buyer → admin → seller enquiry flow, `/api/buyer/enquiries/*`.
 * The admin half answers under `/api/admin/buyer-enquiries/*` and is NOT in this slice.
 *
 * Envelope variance: list and detail answer `{ success, message: 'OK', data }`, create answers
 * 201 `{ data: { id } }`, and respond answers `{ success, message }` with no `data` key.
 * Normalizers are module-scope, take `unknown` and are TOTAL.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const envelope = (response: unknown): Record<string, unknown> => asRecord(asRecord(response).data);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

/** An id the UI hands back to the API is never invented — absent means `null`. */
const asId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

/** `null` rather than `{}` for an absent object, so a component can tell "missing" from "empty". */
const asNullableRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

export const normalizeEnquiryList = (response: unknown): BuyerEnquiryRow[] => {
  const data = asRecord(response).data;
  const items = Array.isArray(data) ? data : asRecord(data).items;
  return Array.isArray(items) ? items.map(asRecord) : [];
};

export function normalizeEnquiryDetail(response: unknown): BuyerEnquiryDetail {
  const data = envelope(response);
  return {
    enquiry: asNullableRecord(data.enquiry),
    property: asNullableRecord(data.property),
    negotiation: asNullableRecord(data.negotiation),
    activity: Array.isArray(data.activity) ? data.activity.map(asRecord) : []
  };
}

export function normalizeCreatedEnquiry(response: unknown): CreatedBuyerEnquiry {
  return { id: asId(envelope(response).id) };
}

/** `okMessage` answers `{ success, message }` with no `data`, so only the message is available. */
export function normalizeMessage(response: unknown, fallback = ''): string {
  return asString(asRecord(response).message, fallback);
}

export const buyerEnquiryApi = createApi({
  reducerPath: 'buyerEnquiryApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['BuyerEnquiry'],
  endpoints: (builder) => ({
    /** `status` is sent only when set — the axios service omitted the whole params object. */
    listBuyerEnquiries: builder.query<BuyerEnquiryRow[], { status?: string }>({
      query: ({ status }) => ({
        url: 'buyer/enquiries',
        params: status ? { status } : undefined
      }),
      transformResponse: (response: unknown) => normalizeEnquiryList(response),
      providesTags: [{ type: 'BuyerEnquiry', id: 'LIST' }]
    }),
    getBuyerEnquiry: builder.query<BuyerEnquiryDetail, { id: number }>({
      query: ({ id }) => ({ url: `buyer/enquiries/${id}` }),
      transformResponse: (response: unknown) => normalizeEnquiryDetail(response),
      providesTags: (_result, _error, arg) => [{ type: 'BuyerEnquiry', id: String(arg.id) }]
    }),
    createBuyerEnquiry: builder.mutation<CreatedBuyerEnquiry, CreateBuyerEnquiryArgs>({
      query: (body) => ({ url: 'buyer/enquiries', method: 'POST', body }),
      transformResponse: (response: unknown) => normalizeCreatedEnquiry(response),
      invalidatesTags: [{ type: 'BuyerEnquiry', id: 'LIST' }],
      /**
       * The property surfaces carry the buyer's enquiry state (`enquiry` on every browse row,
       * and the whole `enquiry` block on the detail), so a new enquiry has to reach `buyerApi`.
       */
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            buyerApi.util.invalidateTags([
              { type: 'Property' as const, id: String(arg.propertyId) },
              { type: 'Property' as const, id: 'LIST' },
              { type: 'Property' as const, id: 'FEED' },
              { type: 'DashboardStats' as const, id: 'STATS' }
            ])
          );
        } catch {
          /* nothing changed server-side */
        }
      }
    }),
    respondBuyerEnquiry: builder.mutation<string, RespondBuyerEnquiryArgs>({
      query: ({ id, message }) => ({
        url: `buyer/enquiries/${id}/respond`,
        method: 'POST',
        body: { message }
      }),
      transformResponse: (response: unknown) => normalizeMessage(response),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'BuyerEnquiry', id: String(arg.id) },
        { type: 'BuyerEnquiry', id: 'LIST' }
      ]
    })
  })
});

export const {
  useListBuyerEnquiriesQuery,
  useGetBuyerEnquiryQuery,
  useCreateBuyerEnquiryMutation,
  useRespondBuyerEnquiryMutation
} = buyerEnquiryApi;
